import { hierarchy, tree } from "d3-hierarchy";

export const CARD_W = 232;
export const CARD_H = 96;

export function buildTree(employees, rootEmployeeId) {
  const active = employees.filter((e) => e.status === "active");
  const byEid = Object.fromEntries(active.map((e) => [e.employee_id, e]));
  let roots;
  if (rootEmployeeId) {
    const root = active.find((e) => e.id === rootEmployeeId);
    roots = root ? [root] : [];
  } else {
    roots = active.filter((e) => !e.manager_employee_id || !byEid[e.manager_employee_id]);
  }
  const childrenOf = {};
  active.forEach((e) => {
    if (e.manager_employee_id && byEid[e.manager_employee_id]) {
      (childrenOf[e.manager_employee_id] ||= []).push(e);
    }
  });
  Object.values(childrenOf).forEach((arr) => arr.sort((a, b) => a.full_name.localeCompare(b.full_name)));
  return { roots, childrenOf, byEid };
}

export function countDescendants(emp, childrenOf) {
  let n = 0;
  const stack = [...(childrenOf[emp.employee_id] || [])];
  while (stack.length) {
    n++;
    const c = stack.pop();
    stack.push(...(childrenOf[c.employee_id] || []));
  }
  return n;
}

function toD3(emp, childrenOf, collapsed) {
  const kids = collapsed.has(emp.id) ? [] : (childrenOf[emp.employee_id] || []);
  return { emp, children: kids.map((k) => toD3(k, childrenOf, collapsed)) };
}

export function computeLayout(employees, { layout, collapsed, rootEmployeeId }) {
  const { roots, childrenOf } = buildTree(employees, rootEmployeeId);
  if (!roots.length) return { nodes: [], edges: [] };

  const virtual = { emp: { id: "__root__", employee_id: "__root__" }, children: roots.map((r) => toD3(r, childrenOf, collapsed)) };
  const rootData = roots.length === 1 ? virtual.children[0] : virtual;
  const h = hierarchy(rootData, (d) => d.children);

  const nodes = [];
  const edges = [];
  const push = (d, x, y) => {
    const emp = d.data.emp;
    if (emp.id === "__root__") return;
    nodes.push({
      id: emp.id,
      position: { x, y },
      data: {
        emp,
        childCount: (childrenOf[emp.employee_id] || []).length,
        hiddenCount: collapsed.has(emp.id) ? countDescendants(emp, childrenOf) : 0,
        collapsed: collapsed.has(emp.id),
      },
      type: "employee",
    });
    if (d.parent && d.parent.data.emp.id !== "__root__") {
      edges.push({
        id: `e-${d.parent.data.emp.id}-${emp.id}`,
        source: d.parent.data.emp.id,
        target: emp.id,
        type: "smoothstep",
        style: { stroke: "#B4B8CC", strokeWidth: 1.5 },
      });
    }
  };

  if (layout === "radial") {
    const maxDepth = Math.max(1, h.height);
    tree().size([2 * Math.PI, maxDepth * 250])(h);
    h.each((d) => {
      const r = d.depth * 250;
      const a = d.x - Math.PI / 2;
      push(d, Math.cos(a) * r, Math.sin(a) * r);
    });
    edges.forEach((e) => { e.type = "straight"; });
  } else if (layout === "horizontal") {
    tree().nodeSize([CARD_H + 44, CARD_W + 110])(h);
    h.each((d) => push(d, d.y, d.x));
  } else {
    tree().nodeSize([CARD_W + 48, CARD_H + 84])(h);
    h.each((d) => push(d, d.x, d.y));
  }
  return { nodes, edges };
}
